import { FC, useCallback, useEffect, useState } from 'react';
import {
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Box,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button } from '@gisce/oficina-virtual-components';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import * as EmailValidator from 'email-validator';
import { styled } from '@mui/material/styles';
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';
import { fetchElectronicNotificationPdf } from '@/components/Contracts/services/electronicNotification';

const NoFlagMuiTelInputStyled = styled(MuiTelInput)`
  .MuiTelInput-IconButton {
    display: none;
  }
`;

interface ContractElectronicNotificationDialogProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (email: string, mobile: string, scope: 'current' | 'all', signature: boolean) => void;
  submitted: boolean;
  sending: boolean;
  children?: React.ReactNode;
  profile?: { data?: { email?: string; mobile?: string } };
  status: 'active' | 'requested' | 'unactive'
}

const ContractElectronicNotificationDialog: FC<ContractElectronicNotificationDialogProps> = ({
  open,
  handleClose,
  handleSubmit,
  submitted,
  sending,
  children,
  profile,
  status
}) => {
  const { t } = useTranslation();
  const Settings = useSettingsContext();

  const [scope, setScope] = useState<'current' | 'all'>('all');
  const [signature, setSignature] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [documentBlob, setDocumentBlob] = useState<Blob | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  

  const schema = z.object({
    email: z
      .string()
      .min(1, t('common:text.required_field'))
      .refine((val) => EmailValidator.validate(val), {
        message: t('common:text.contractation_email_not_valid'),
      }),
    mobile: z
      .string()
      .min(4, t('common:text.required_field'))
      .refine((tel) => matchIsValidTel(tel), {
        message: t('common:text.lead_contact_phone_not_valid'),
      }),
    hasRead: z.literal(true, {
      errorMap: () => ({ message: t('common:text.electronic_notification_read_required') })
    })
  });

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: profile?.data?.email || '',
      mobile: profile?.data?.mobile || '',
      hasRead: false,
    },
  });

  const {
    handleSubmit: onSubmit,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const onFormSubmit = (data: { email: string; mobile: string }) => {
    handleSubmit(data.email, data.mobile, scope, signature);
  };

  const fetchDocument = useCallback(async () => {
    setLoading(true);
    setError(false);
    setDocumentBlob(null);

    try {
      const blob = await fetchElectronicNotificationPdf();
      setDocumentBlob(blob);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }

  }, [])

  useEffect(() => {
    if (!documentBlob) {
      fetchDocument();
    }
  }, []);

  useEffect(() => {
    if (!documentBlob) {
      setObjectUrl(null);
      return;
    }

    const nextObjectUrl = URL.createObjectURL(documentBlob);
    setObjectUrl(nextObjectUrl);

    return () => {
      URL.revokeObjectURL(nextObjectUrl);
    };
  }, [documentBlob]);


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth >
      <DialogTitle>
        {t('common:text.electronic_notification_title')}
      </DialogTitle>

      {status === 'unactive' && (
        <FormProvider {...methods}>
          <form onSubmit={onSubmit(onFormSubmit)}>
            <DialogContent sx={{paddingY: 0}}>
              {children || (
                <>
                  {loading && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2
                      }}
                    >
                      <CircularProgress />
                      <Typography variant="body1" color="text.secondary">
                        {t('common:text.electronic_notification_document_loading')}
                      </Typography>
                    </Box>
                  )}

                  {error && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2
                      }}
                    >
                      <Typography variant="body1" color="error">
                        {t('common:text.electronic_notification_document_error')}
                      </Typography>
                    </Box>
                  )}

                  {!loading && !error && objectUrl && (
                    <div>
                      <Box
                        sx={{
                          width: '100%',
                          height: '50vh',
                          overflow: 'hidden',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 2
                        }}
                      >
                        <iframe
                          src={objectUrl}
                          title={t('common:text.electronic_notification_document_title')}
                          style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            border: 'none'
                          }}
                        />
                      </Box>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('common:text.electronic_notification_description')}
                      </Typography>
                      <Grid container>
                        <Grid container item xs={12} direction='row' spacing={2}>
                          <Grid item md={6} xs={12}>
                            <Controller
                              name="email"
                              control={control}
                              render={({ field, fieldState }) => (
                                <>
                                  <TextField
                                    {...field}
                                    label={t('common:text.contract_contact_data_email') + "*"}
                                    fullWidth
                                    error={!!errors.email}
                                  />
                                  {errors.email && (
                                    <FormHelperText error>
                                      {errors.email.message?.toString()}
                                    </FormHelperText>
                                  )}
                                </>
                              )}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <Controller
                              name="mobile"
                              control={control}
                              render={({ field, fieldState }) => (
                                <NoFlagMuiTelInputStyled
                                  {...field}
                                  label={t('common:text.contract_contact_data_phone') + "*"}
                                  defaultCountry="ES"
                                  fullWidth
                                  error={!!errors.mobile}
                                  helperText={fieldState.isTouched ? errors.mobile?.message : ''}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>

                        <Grid item xs={12} sx={{mt: 2}}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontSize: 12}}>
                              {t('common:text.electronic_notification_scope_label')}
                            </FormLabel>
                            <RadioGroup
                              row
                              value={scope}
                              onChange={(e) => setScope(e.target.value as 'current' | 'all')}
                            >
                              <FormControlLabel
                                value="current"
                                control={<Radio />}
                                label={t('common:text.electronic_notification_current')}
                              />
                              <FormControlLabel
                                value="all"
                                control={<Radio />}
                                label={t('common:text.electronic_notification_all')}
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>

                        <Grid item container xs={12}>
                            <Grid item xs={12}>
                              {Settings.features.signatureElectronicNotification && (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={signature}
                                      onChange={() => setSignature(!signature)}
                                    />
                                  }
                                  label={t('common:text.electronic_notification_signature')}
                                />
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name="hasRead"
                                control={control}
                                render={({ field }) => (
                                  <>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={field.value}
                                          onChange={(e) => field.onChange(e.target.checked)}
                                        />
                                      }
                                      label={t('common:text.electronic_notification_read_confirmation')}
                                    />
                                    {errors.hasRead && (
                                      <FormHelperText error>
                                        {errors.hasRead.message?.toString()}
                                      </FormHelperText>
                                    )}
                                  </>
                                )}
                              />
                            </Grid>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              {!submitted ? (
                <>
                  <Button variant="outlined" color="primary" onClick={handleClose}>
                    {t('common:text.electronic_notification_cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={sending || isSubmitting || error || loading}
                  >
                    {sending || isSubmitting ? <CircularProgress size={20} /> : null}
                    {t('common:text.electronic_notification_request')}
                  </Button>
                </>
              ) : (
                <Button variant="outlined" color="primary" onClick={handleClose}>
                  {t('common:text.electronic_notification_close')}
                </Button>
              )}
            </DialogActions>
          </form>
        </FormProvider>
      )}

      {status === 'requested' && (
        <>
          <DialogContent sx={{paddingY: 0}}>
            <Typography variant="body1">
              {t('common:text.electronic_notification_process_already')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button variant="outlined" color="primary" onClick={handleClose}>
                {t('common:text.electronic_notification_close')}
              </Button>
          </DialogActions>
        </>
      )}

      {status === 'active' && (
        <>
          <DialogContent sx={{paddingY: 0}}>
            <Typography variant="body1">
              {t('common:text.electronic_notification_enabled_title')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button variant="outlined" color="primary" onClick={handleClose}>
                {t('common:text.electronic_notification_close')}
              </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ContractElectronicNotificationDialog;
